import {
  Chart,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
} from "chart.js";
import { SankeyController, Flow } from "chartjs-chart-sankey";
import { getRootNodes } from "./energyReportData";

Chart.register(
  SankeyController,
  Flow,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
);

const COLOR_PALETTE = [
  "#009f8d",
  "#ff9c07",
  "#1e88e5",
  "#8e24aa",
  "#0ea5e9",
  "#facc15",
  "#00c6a9",
  "#ffb74d",
];

const COL_ROOT = 0;
const COL_MAIN = 1;
const COL_LEAF = 2;

const getNodeColor = (depth) =>
  COLOR_PALETTE[(depth - 1) % COLOR_PALETTE.length];

function parseEnergy(value) {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = parseFloat(value.replace(/[^\d.-]/g, ""));
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

export function formatKwhLabel(value) {
  return Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
}

function formatPercent(ratio) {
  if (!Number.isFinite(ratio) || ratio <= 0) return "0.0%";
  return `${(ratio * 100).toFixed(1)}%`;
}

function normalizeCategory(node) {
  return (node?.category || "").toUpperCase();
}

export function isGeneratorLike(node) {
  if (!node) return false;
  if (normalizeCategory(node) === "GENERATOR") return true;
  return /generator/i.test(node.machine_name || "");
}

function isEgaugeLike(node) {
  if (!node) return false;
  return /egauge/i.test(node.machine_name || "");
}

function nudgeAuxiliaryNodeLabels(chart, labelMap) {
  const controller = chart.getDatasetMeta(0)?.controller;
  const nodes = controller?._nodes;
  const { xScale, yScale } = controller?._cachedMeta || {};
  if (!nodes || !xScale || !yScale) return;

  const auxNodes = [...nodes.entries()]
    .filter(([key]) =>
      isGeneratorLike({ machine_name: key }) || isEgaugeLike({ machine_name: key }),
    )
    .map(([key, node]) => ({ key, node }))
    .sort((a, b) => (a.node.priority ?? 0) - (b.node.priority ?? 0));

  if (auxNodes.length === 0) return;

  const ctx = chart.ctx;
  const nodeWidth = controller.options.nodeWidth ?? 24;
  const fontSize = controller.options.font?.size ?? 11;
  const lineHeight = fontSize + 2;
  const minGap = lineHeight * 2 + 8;

  const positions = auxNodes.map(({ key, node }) => {
    const x = xScale.getPixelForValue(node.x);
    const y = yScale.getPixelForValue(node.y);
    const span = Math.max(node.in || node.out, node.out || node.in);
    const height = Math.abs(yScale.getPixelForValue(node.y + span) - y);
    return {
      key,
      textX: x + nodeWidth + 6,
      middle: y + height / 2,
    };
  });

  for (let i = 1; i < positions.length; i += 1) {
    if (positions[i].middle - positions[i - 1].middle < minGap) {
      positions[i].middle = positions[i - 1].middle + minGap;
    }
  }

  ctx.save();
  ctx.fillStyle = "#000000";
  ctx.font = `bold ${fontSize}px Helvetica, Arial, sans-serif`;
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  positions.forEach(({ key, textX, middle }) => {
    const lines = (labelMap[key] || key).split("\n");
    const top = middle - (lines.length * lineHeight) / 2 + lineHeight / 2;
    lines.forEach((line, index) => {
      ctx.fillText(line, textX, top + index * lineHeight);
    });
  });
  ctx.restore();
}

export function buildSankeyChartConfig(
  hierarchyData,
  scopeNodeName,
  maxLevels = 4,
  options = {},
) {
  const { nodeWidth = 28, nodePadding = 28 } = options;

  if (!hierarchyData || typeof hierarchyData !== "object") {
    return {
      chartData: { datasets: [{ data: [] }] },
      nodeStyles: {},
      flowRows: [],
      labelMap: {},
    };
  }

  const isWholeFacility = !scopeNodeName || scopeNodeName === "Usage";
  const rootNodes = isWholeFacility
    ? getRootNodes(hierarchyData)
    : hierarchyData[scopeNodeName]
      ? [hierarchyData[scopeNodeName]]
      : [];

  const branchRootId = rootNodes[0]?.machine_name || null;

  const rawPoints = [];
  const nodeStyles = {};
  const labelMap = {};
  const columnMap = {};
  const priorityMap = {};
  const processedNodes = new Set();

  const registerNode = (node, depth, columnIndex) => {
    const nodeId = node.machine_name;
    if (!nodeId) return;

    const nodeEnergy = parseEnergy(node.energy);
    if (nodeEnergy <= 0 && !node.children?.length) return;

    nodeStyles[nodeId] = {
      label: `${node.machine_name} (${formatKwhLabel(nodeEnergy)} kWh)`,
      energy: nodeEnergy,
      color: node.color || getNodeColor(depth),
    };
    columnMap[nodeId] = columnIndex;

    if (normalizeCategory(node) === "GRID" || columnIndex === COL_ROOT) {
      priorityMap[nodeId] = 0;
    } else if (isGeneratorLike(node)) {
      priorityMap[nodeId] = 10;
    } else if (isEgaugeLike(node)) {
      priorityMap[nodeId] = 20;
    } else if (columnIndex === COL_MAIN) {
      priorityMap[nodeId] = 1;
    }
  };

  const traverseNode = (node, depth = 1, columnIndex = COL_ROOT) => {
    if (!node || depth > maxLevels) return;

    const nodeId = node.machine_name;
    if (!nodeId || processedNodes.has(nodeId)) return;
    processedNodes.add(nodeId);

    registerNode(node, depth, columnIndex);

    (node.children || []).forEach((child) => {
      const childId = child.machine_name;
      const childEnergy = parseEnergy(child.energy);
      if (!childId || childEnergy <= 0) return;

      if (isGeneratorLike(child)) {
        const fromId = branchRootId || nodeId;
        rawPoints.push({
          from: fromId,
          to: childId,
          flow: childEnergy,
          realFlow: childEnergy,
        });
        traverseNode(child, depth + 1, COL_MAIN);
        return;
      }

      rawPoints.push({
        from: nodeId,
        to: childId,
        flow: childEnergy,
        realFlow: childEnergy,
      });

      const childColumn =
        columnIndex === COL_ROOT ? COL_MAIN : COL_LEAF;
      traverseNode(child, depth + 1, childColumn);
    });
  };

  rootNodes.forEach((root) => traverseNode(root, 1, COL_ROOT));

  const totalEnergy =
    rootNodes.reduce((sum, node) => sum + parseEnergy(node.energy), 0) ||
    rawPoints.reduce((sum, point) => sum + (point.realFlow || 0), 0);

  Object.entries(nodeStyles).forEach(([nodeId, style]) => {
    const nodeEnergy = style.energy || 0;
    const shareLabel =
      totalEnergy > 0 ? formatPercent(nodeEnergy / totalEnergy) : "0.0%";
    labelMap[nodeId] = `${nodeId}\n${formatKwhLabel(nodeEnergy)} kWh (${shareLabel})`;
  });

  const chartLabelMap = { ...labelMap };
  Object.keys(chartLabelMap).forEach((nodeId) => {
    if (
      isGeneratorLike({ machine_name: nodeId }) ||
      isEgaugeLike({ machine_name: nodeId })
    ) {
      chartLabelMap[nodeId] = "";
    }
  });

  const maxFlow = rawPoints.reduce((max, point) => Math.max(max, point.flow), 0);
  const minVisible = maxFlow > 0 ? maxFlow * 0.12 : 0;
  const minAuxiliaryFlow = maxFlow > 0 ? maxFlow * 0.16 : 0;
  const dataPoints = rawPoints.map((point) => {
    const isAuxiliaryFeed =
      isGeneratorLike({ machine_name: point.to }) ||
      isEgaugeLike({ machine_name: point.to });

    if (isAuxiliaryFeed) {
      return {
        ...point,
        flow: Math.max(point.flow, minAuxiliaryFlow),
      };
    }

    if (point.flow < maxFlow * 0.08) {
      return point;
    }

    return {
      ...point,
      flow: minVisible > 0 ? Math.max(point.flow, minVisible) : point.flow,
    };
  });

  return {
    flowRows: dataPoints,
    nodeStyles,
    labelMap,
    chartData: {
      datasets: [
        {
          label: "Energy Flow",
          data: dataPoints,
          colorFrom: (ctx) => nodeStyles[ctx?.raw?.from]?.color || "#94a3b8",
          colorTo: (ctx) => nodeStyles[ctx?.raw?.from]?.color || "#94a3b8",
          colorMode: "from",
          alpha: 0.65,
          size: "max",
          borderWidth: 0,
          font: { size: 11, weight: "bold" },
          labels: chartLabelMap,
          priority: priorityMap,
          column: columnMap,
          node: { width: nodeWidth, padding: nodePadding },
        },
      ],
    },
  };
}

export function renderSankeyChartImage(hierarchyData, scopeNodeName, maxLevels = 4) {
  const { chartData, flowRows, labelMap } = buildSankeyChartConfig(
    hierarchyData,
    scopeNodeName,
    maxLevels,
    { nodeWidth: 24, nodePadding: 52 },
  );

  if (!flowRows.length) {
    const canvas = document.createElement("canvas");
    canvas.width = 900;
    canvas.height = 320;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "#64748b";
    ctx.font = "16px Helvetica, Arial, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText(
      "Not enough hierarchy data to build Sankey diagram.",
      canvas.width / 2,
      canvas.height / 2,
    );
    return canvas.toDataURL("image/png");
  }

  const canvas = document.createElement("canvas");
  canvas.width = 1000;
  canvas.height = 680;

  const chart = new Chart(canvas, {
    type: "sankey",
    data: chartData,
    options: {
      responsive: false,
      animation: false,
      plugins: {
        legend: { display: false },
      },
    },
  });

  chart.draw();
  nudgeAuxiliaryNodeLabels(chart, labelMap);
  const image = canvas.toDataURL("image/png");
  chart.destroy();
  return image;
}
